import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { MedicationInfo } from 'components/Common/Medication/types'
import { MedicationsFormField } from './formField'
import { createAnswerItemsFromValues, getAnswerValuesFromItems } from '../../../helpers'
import { formatAssessmentMedications } from '../../../hooks/helpers'
import { AdapterOptions, SectionAdapter } from '../../../adapters/types'
import { FlatAnswerMutation } from '../../../adapters/types'
import { RecertFormData } from '../../types/RecertFormData'
import { RecertAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { AllQuestionnaireItemType } from '../../../hooks/constants'

/**
 * Medications subsection adapter
 */
export class MedicationsAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const medications = formatAssessmentMedications(flatAnswers)

    const medicationsSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY
    )?.at(0)

    const [initialsAuthorization] = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICATIONS_INITIALS_AUTHORIZATION
    )

    return {
      [MedicationsFormField.MEDICATIONS]: medications,
      [MedicationsFormField.MEDICATION_SUMMARY]: medicationsSummary ?? '',
      [MedicationsFormField.MEDICATIONS_INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(formData: RecertFormData): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_SUMMARY,
        value: formData[MedicationsFormField.MEDICATION_SUMMARY]
      }
    ]

    // Handle medications list using existing pattern
    const formMedications = formData[MedicationsFormField.MEDICATIONS] as
      | MedicationInfo[]
      | undefined
    if (formMedications && Array.isArray(formMedications)) {
      // Reverse the order to match most recent first
      const medicationsAnswers = [...formMedications].reverse().map((medication) => {
        const questionnaireItemsAndValues: [AllQuestionnaireItemType, unknown][] = [
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DRUGBANK_PCID,
            medication.productConceptId
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_NAME,
            medication.name
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_ROUTE,
            medication.route
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_NUMBER,
            medication.strength?.number
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_STRENGTH_UNIT,
            medication.strength?.unit
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INDICATIONS,
            medication.indications
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_FREQUENCY,
            medication.frequency
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_NUMBER,
            medication.dosage?.number
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DOSAGE_UNIT,
            medication.dosage?.unit
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INSTRUCTIONS,
            medication.instructions
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_USAGE_STATUS,
            medication.usageStatus
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_START_DATE,
            medication.startDate
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_DISCONTINUE_DATE,
            medication.discontinueDate
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_CONTROLLED,
            medication.controlled
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_LIQUID,
            medication.liquid
          ],
          [
            RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO_INFECTION,
            medication.infection
          ]
        ]

        return {
          item: createAnswerItemsFromValues(questionnaireItemsAndValues)
        }
      })

      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICATIONS_MEDICATION_LIST_MEDICATION_INFO,
        value: medicationsAnswers
      })
    }

    // Build initials authorization mutation
    if (formData[MedicationsFormField.MEDICATIONS_INITIALS_AUTHORIZATION]) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICATIONS_INITIALS_AUTHORIZATION,
        value: formData[MedicationsFormField.MEDICATIONS_INITIALS_AUTHORIZATION]
      })
    }

    return mutations
  }
}
